import { useTranslation } from "react-i18next";
import { useEffect, useMemo } from "react";
import { Minus, Plus, Trash2 } from "lucide-react";
import { useFormContext, useWatch } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { ControlledSelectV2 } from "@/components/ui/customs/controlled-select-v2";
import { ControlledUnifiedInput } from "@/components/ui/customs/controlled-unified-input";
import { volumesOptionsWoodBox, VolumesWoodBoxAvailable } from "@/constants/options/volumes.option";
import { QuotationUpdateBoFormData } from "@/types/forms/bo/expeditionBo.form";
import { PackagingRowProps } from "../../../expeditionForm.types";

export const PackagingRow = ({ index, onRemove }: PackagingRowProps) => {
  const { t } = useTranslation("boExpeditionForm");
  const { control, setValue, getValues } = useFormContext<QuotationUpdateBoFormData>();

  const bottleVolume = useWatch({
    control,
    name: `quotationWoodBox.${index}.bottleVolume`,
  });

  const boxQuantity = useWatch({
    control,
    name: `quotationWoodBox.${index}.boxQuantity`,
  });

  const bottleQuantity = useWatch({
    control,
    name: `quotationWoodBox.${index}.bottleQuantity`,
  });

  const bottleQuantityOptions = useMemo(
    () =>
      VolumesWoodBoxAvailable.find((v) => v.value === Number(bottleVolume))?.available.map(
        (quantity) => ({
          label: quantity.toString(),
          value: quantity.toString(),
        }),
      ) ?? [],
    [bottleVolume],
  );

  useEffect(() => {
    const current = getValues(`quotationWoodBox.${index}.bottleQuantity`);
    if (
      current !== undefined &&
      current !== null &&
      bottleQuantityOptions.length > 0 &&
      !bottleQuantityOptions.some((opt) => opt.value === current?.toString())
    ) {
      setValue(`quotationWoodBox.${index}.bottleQuantity`, "" as unknown as number, {
        shouldDirty: true,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bottleQuantityOptions]);

  const addBoxQuantity = (quantity: number) => {
    const current = getValues(`quotationWoodBox.${index}.boxQuantity`);
    if (current == null || current == undefined) {
      setValue(`quotationWoodBox.${index}.boxQuantity`, 1, {
        shouldDirty: true,
      });
      return;
    }
    const newQuantity = current + quantity;
    if (newQuantity < 1) {
      setValue(`quotationWoodBox.${index}.boxQuantity`, 1, {
        shouldDirty: true,
      });
      return;
    }
    setValue(`quotationWoodBox.${index}.boxQuantity`, newQuantity, {
      shouldDirty: true,
    });
  };

  return (
    <div className="grid grid-cols-2 flex-row items-center gap-2 rounded-lg bg-slate-200 p-2 @xl:flex">
      <ControlledSelectV2
        control={control}
        name={`quotationWoodBox.${index}.bottleVolume`}
        options={volumesOptionsWoodBox.map((volume) => ({
          label: volume.label,
          value: volume.value.toString(),
        }))}
        setValue={(value) =>
          setValue(`quotationWoodBox.${index}.bottleVolume`, Number(value), {
            shouldDirty: true,
            shouldValidate: true,
          })
        }
        wrapperClassName="w-full"
        label={t("packagingRow.volumeLabel")}
        required
      />
      <ControlledSelectV2
        control={control}
        name={`quotationWoodBox.${index}.bottleQuantity`}
        options={bottleQuantityOptions}
        setValue={(value) =>
          setValue(`quotationWoodBox.${index}.bottleQuantity`, Number(value), {
            shouldDirty: true,
            shouldValidate: true,
          })
        }
        wrapperClassName="w-full"
        label={t("packagingRow.bottlesPerBoxLabel")}
        required
      />
      <div className="flex w-fit flex-row items-center gap-0">
        <Button
          className="hidden h-[56px] sm:block"
          variant="outline"
          type="button"
          onClick={() => addBoxQuantity(-1)}
        >
          <Minus className="h-4 w-4" />
        </Button>
        <ControlledUnifiedInput
          control={control}
          name={`quotationWoodBox.${index}.boxQuantity`}
          wrapperClassName="sm:w-[60px] w-full h-[56px]"
          inputType="integer"
        />
        <Button
          className="hidden h-[56px] sm:block"
          variant="outline"
          type="button"
          onClick={() => addBoxQuantity(1)}
        >
          <Plus className="h-4 w-4" />
        </Button>
      </div>
      <div className="flex w-full flex-col items-center justify-center">
        <p className="w-fit text-sm text-nowrap text-gray-500">
          {t("packagingRow.boxSummary", {
            count: boxQuantity,
            bottleCount: bottleQuantity * boxQuantity,
          })}
        </p>
        <p className="w-fit text-sm text-nowrap text-gray-500">
          {t("packagingRow.format", { volume: bottleVolume })}
        </p>
      </div>
      <Button
        className="h-fit w-fit px-1 [&_svg]:h-6 [&_svg]:w-6"
        variant="ghost"
        type="button"
        onClick={onRemove}
      >
        <Trash2 className="h-6 w-6 text-red-600" />
      </Button>
    </div>
  );
};
